import {
    BASE_VALUE,
    BulkSelectionContextValue,
} from '@/components/bulkQueue/tables/BulkSelectionContext';
import {
    Entity,
    useBulkSelectionStore,
    useBulkState,
} from '@/components/bulkQueue/tables/bulkContextUtils';
import { BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateAssignmentTable';
import {
    BulkTentativeDateRecommendationsQuery,
    BulkTentativeDateRecommendationsQueryVariables,
} from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateContext.gql.types';
import {
    VISIT_RECOMMENDED_DATES_FRAGMENT,
    VISIT_REQUEST_RECOMMENDED_DATES_FRAGMENT,
} from '@/components/decisionSupport/queries/dateRecommendations';
import { DateRecommendationFragment } from '@/components/decisionSupport/queries/dateRecommendations.gql.types';
import {
    BULK_UPDATE_TENTATIVE_DATE_MUTATION,
    BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION,
} from '@/components/visitRequests/mutations/updateTentativeDate';
import {
    BulkUpdateVisitRequestTentativeDateMutation,
    BulkUpdateVisitRequestTentativeDateMutationVariables,
    BulkUpdateVisitTentativeDateMutation,
    BulkUpdateVisitTentativeDateMutationVariables,
} from '@/components/visitRequests/mutations/updateTentativeDate.gql.types';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { errorToast } from '@/toast';
import {
    ProviderStore,
    ProviderWritableStore,
    createDefaultProviderStore,
    createDefaultWritableProviderStore,
    createStore,
    useStoreValue,
} from '@/utils/store';
import { gql, useMutation, useQuery } from '@apollo/client';
import { UpdateVisitRequestTentativeDateInput, UpdateVisitTentativeDateInput } from 'gql-types';
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

export const BULK_TENTATIVE_DATE_RECOMMENDATIONS = gql`
    query BulkTentativeDateRecommendations(
        $visitIds: [UUID!]!
        $excludeVisits: Boolean!
        $visitRequestIds: [UUID!]!
        $excludeVisitRequests: Boolean!
    ) {
        decisionSupport {
            visitRequests(filters: { id: { inList: $visitRequestIds } })
                @skip(if: $excludeVisitRequests) {
                __typename
                dateRecommendations {
                    ...VisitRequestRecommendedDates
                }
            }
            visits(filters: { id: { inList: $visitIds } }) @skip(if: $excludeVisits) {
                __typename
                dateRecommendations {
                    ...VisitRecommendedDates
                }
            }
        }
    }
    ${VISIT_RECOMMENDED_DATES_FRAGMENT}
    ${VISIT_REQUEST_RECOMMENDED_DATES_FRAGMENT}
`;

type BulkTentativeDateContextValue = BulkSelectionContextValue<{
    recommendationsStore: ProviderStore<DateRecommendationFragment[]>;
    selectedRecommendationStore: ProviderWritableStore<string | null>;
}>;

const DEFAULT_VALUE: BulkTentativeDateContextValue = {
    ...BASE_VALUE,
    recommendationsStore: createDefaultProviderStore<DateRecommendationFragment[]>([]),
    selectedRecommendationStore: createDefaultWritableProviderStore<string | null>(null),
};

export const useUpdateTentativeDates = () => {
    return useMutation<
        BulkUpdateVisitRequestTentativeDateMutation,
        BulkUpdateVisitRequestTentativeDateMutationVariables
    >(BULK_UPDATE_TENTATIVE_DATE_MUTATION, {
        onError: (error) => {
            errorToast(error.message);
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
        awaitRefetchQueries: true,
    });
};

export const useUpdateVisitTentativeDates = () => {
    return useMutation<
        BulkUpdateVisitTentativeDateMutation,
        BulkUpdateVisitTentativeDateMutationVariables
    >(BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION, {
        onError: (error) => {
            errorToast(error.message);
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
        awaitRefetchQueries: true,
    });
};

const BulkTentativeDateContext = createContext<BulkTentativeDateContextValue>(DEFAULT_VALUE);

type BulkTentativeDateContextProviderProps = {
    children: ReactNode;
    entities: Entity[];
};

export const BulkTentativeDateContextProvider = (props: BulkTentativeDateContextProviderProps) => {
    const { children, entities } = props;
    const [isEntitySelectedStore, setValue, selectedCount, resetSelection] =
        useBulkSelectionStore();
    const recommendationsStoreRef = useRef(createStore<DateRecommendationFragment[]>());
    const recommendationsStore = recommendationsStoreRef.current;
    const selectedRecommendationStoreRef = useRef(createStore<string | null>());
    const selectedRecommendationStore = selectedRecommendationStoreRef.current;

    const visitIds = useMemo(
        () => entities.filter((entity) => entity.__typename === 'Visit').map((entity) => entity.id),
        [entities],
    );

    const visitRequestIds = useMemo(
        () =>
            entities
                .filter((entity) => entity.__typename === 'VisitRequest')
                .map((entity) => entity.id),
        [entities],
    );

    const excludeVisits = visitIds.length === 0;
    const excludeVisitRequests = visitRequestIds.length === 0;

    const { data, previousData, loading } = useQuery<
        BulkTentativeDateRecommendationsQuery,
        BulkTentativeDateRecommendationsQueryVariables
    >(BULK_TENTATIVE_DATE_RECOMMENDATIONS, {
        variables: {
            visitIds,
            excludeVisits,
            visitRequestIds,
            excludeVisitRequests,
        },
        fetchPolicy: 'network-only',
    });

    const currentData = data ?? previousData;
    const hasInitializedRef = useRef(false);

    useEffect(() => {
        const recommendations = new Map<string, DateRecommendationFragment[]>();
        const selectedDates = new Map<string, string | null>();

        const dateRecommendations = [
            ...(currentData?.decisionSupport.visits?.dateRecommendations ?? []),
            ...(currentData?.decisionSupport.visitRequests?.dateRecommendations ?? []),
        ];
        for (const rec of dateRecommendations) {
            const entityId = 'visitRequest' in rec ? rec.visitRequest.id : rec.visit.id;
            const dates = rec.recommendedDates;
            recommendations.set(entityId, dates);

            // Auto-select the best recommendation
            const bestDate = dates
                .slice()
                .sort((a, b) => a.recommendationRank - b.recommendationRank)
                .at(0);
            if (bestDate) {
                selectedDates.set(entityId, GraphInputHelpers.date(bestDate.date));
                // Auto-select the entity for submission only on initial load
                if (!hasInitializedRef.current) {
                    setValue(entityId, true);
                }
            }
        }

        recommendationsStore.setAll(recommendations);
        selectedRecommendationStore.setAll(selectedDates);
        hasInitializedRef.current = true;
    }, [currentData, recommendationsStore, selectedRecommendationStore, setValue]);

    const [mutateVisitRequests, { loading: submittingVisitRequests }] = useUpdateTentativeDates();
    const [mutateVisits, { loading: submittingVisits }] = useUpdateVisitTentativeDates();
    const submitting = submittingVisitRequests || submittingVisits;

    const entityTypeMap = useMemo(
        () => new Map(entities.map((e) => [e.id, e.__typename])),
        [entities],
    );

    const submit = useCallback(async () => {
        const selected = isEntitySelectedStore.getAll();
        const visitRequests: UpdateVisitRequestTentativeDateInput[] = [];
        const visits: UpdateVisitTentativeDateInput[] = [];

        selected.forEach((isSelected: boolean, entityId: string) => {
            if (!isSelected) return;
            const date = selectedRecommendationStore.getSnapshot(entityId);
            if (!date) return;

            if (entityTypeMap.get(entityId) === 'Visit') {
                visits.push({ visitId: entityId, tentativeDate: date });
            } else {
                visitRequests.push({ visitRequestId: entityId, tentativeDate: date });
            }
        });

        const mutations = [];
        if (visitRequests.length > 0) {
            mutations.push(mutateVisitRequests({ variables: { input: { visitRequests } } }));
        }
        if (visits.length > 0) {
            mutations.push(mutateVisits({ variables: { input: { visits } } }));
        }
        await Promise.all(mutations);
        resetSelection();
    }, [
        mutateVisitRequests,
        mutateVisits,
        isEntitySelectedStore,
        selectedRecommendationStore,
        resetSelection,
        entityTypeMap,
    ]);

    const state = useBulkState(loading, submitting);

    const value: BulkTentativeDateContextValue = {
        selectedRecommendationStore: {
            ...selectedRecommendationStore,
            setValue: selectedRecommendationStore.setValue,
        },
        recommendationsStore,
        isEntitySelectedStore: {
            ...isEntitySelectedStore,
            setValue,
        },
        submit,
        state,
        selectedCount,
        entityCount: entities.length,
    };

    return (
        <BulkTentativeDateContext.Provider value={value}>
            {children}
        </BulkTentativeDateContext.Provider>
    );
};

const getSelectableEntityIds = (
    recommendationsStore: ProviderStore<DateRecommendationFragment[]>,
): string[] => {
    const ids: string[] = [];
    recommendationsStore.getAll().forEach((recommendations, entityId) => {
        if (recommendations.length > 0) {
            ids.push(entityId);
        }
    });
    return ids;
};

export const useBulkTentativeDateContext = () => {
    const {
        submit,
        state,
        selectedCount,
        entityCount,
        recommendationsStore,
        isEntitySelectedStore,
    } = useContext(BulkTentativeDateContext);
    const summary = useTentativeDateSummary();

    const selectableCount = useSyncExternalStore(
        (cb: () => void) => recommendationsStore.subscribeToAll(cb),
        () => getSelectableEntityIds(recommendationsStore).length,
    );

    const selectAll = useCallback(() => {
        const selectableIds = getSelectableEntityIds(recommendationsStore);
        const currentlySelected = isEntitySelectedStore.getAll();
        const allSelected =
            selectableIds.length > 0 &&
            selectableIds.every((id) => currentlySelected.get(id) === true);
        selectableIds.forEach((id) => {
            isEntitySelectedStore.setValue?.(id, !allSelected);
        });
    }, [recommendationsStore, isEntitySelectedStore]);

    return useMemo(
        () => ({
            submit,
            state,
            selectedCount,
            entityCount,
            summary,
            selectableCount,
            selectAll,
        }),
        [selectedCount, summary, entityCount, state, submit, selectableCount, selectAll],
    );
};

export const useTentativeDateSummary = () => {
    const { selectedRecommendationStore, isEntitySelectedStore } =
        useContext(BulkTentativeDateContext);

    return useSyncExternalStore(
        (cb: () => void) => {
            const unsub1 = selectedRecommendationStore.subscribeToAll(cb);
            const unsub2 = isEntitySelectedStore.subscribeToAll(cb);
            return () => {
                unsub1();
                unsub2();
            };
        },
        () => {
            const dates = new Set<string>();
            const selected = isEntitySelectedStore.getAll();
            selected.forEach((isSelected: boolean, entityId: string) => {
                if (!isSelected) return;
                const date = selectedRecommendationStore.getSnapshot(entityId);
                if (date) {
                    dates.add(date);
                }
            });
            const numSelected = Array.from(selected.values()).filter(Boolean).length;
            const dateList = Array.from(dates).join(', ');
            return `Assign date${dates.size > 1 ? 's' : ''} to ${numSelected} visits: ${dateList}`;
        },
    );
};

export const useDateRecommendations = (
    entityId: string,
): [recommendations: DateRecommendationFragment[], isLoading: boolean] => {
    const { state } = useContext(BulkTentativeDateContext);
    const recommendations = useStoreValue(
        BulkTentativeDateContext,
        'recommendationsStore',
        entityId,
    );

    return [recommendations ?? [], state === 'loading'];
};

export const useIsEntitySelected = (entityId: string): [boolean, (isSelected: boolean) => void] => {
    const { isEntitySelectedStore } = useContext(BulkTentativeDateContext);
    const isSelected = useStoreValue(BulkTentativeDateContext, 'isEntitySelectedStore', entityId);

    const setIsSelected = useCallback(
        (selected: boolean) => {
            isEntitySelectedStore.setValue?.(entityId, selected);
        },
        [isEntitySelectedStore, entityId],
    );

    return [isSelected, setIsSelected];
};

export const useSelectedDate = (
    entityId: string,
): [string | null, (date: string | null) => void] => {
    const { selectedRecommendationStore, isEntitySelectedStore } =
        useContext(BulkTentativeDateContext);
    const selectedDate = useStoreValue(
        BulkTentativeDateContext,
        'selectedRecommendationStore',
        entityId,
    );

    const setSelectedDate = useCallback(
        (date: string | null) => {
            selectedRecommendationStore.setValue(entityId, date);
            // Auto-select the entity when a date is chosen
            if (date) {
                isEntitySelectedStore.setValue?.(entityId, true);
            }
        },
        [selectedRecommendationStore, isEntitySelectedStore, entityId],
    );

    return [selectedDate ?? null, setSelectedDate];
};
